import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  hasFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import type { RecalculoMasivoResultado } from '@/types/crm';
import {
  RECALCULO_LIMIT_MAX,
  describeCambioLead,
  formatNumber,
  summarizeRecalculo,
} from './crm-format';
import {
  RECALCULO_FORM_FIELDS,
  emptyRecalculoForm,
  recalcularMasivoPayload,
  type RecalculoFormState,
} from './crm-payload';
import { validateRecalculoForm } from './crm-validation';
import { useRecalculateLeads } from './use-crm';

/**
 * Recálculo MASIVO del CRM (`POST .../crm/recalcular`, solo ADMIN).
 *
 * El clasificador corre solo cuando entra un mensaje; este endpoint es para lo que ya estaba antes
 * de activar el CRM (o para volver a mirar todos los chats cuando el clasificador cambió). Es una
 * operación pesada —revisa hasta 2000 conversaciones con sus mensajes— así que:
 *  - el cuerpo lleva un TOPE explícito (`limite`, 1-2000) y el diálogo muestra el número que de
 *    verdad va a viajar;
 *  - `incluirManuales` viene apagado: por defecto el recálculo RESPETA los leads que el equipo
 *    reclasificó a mano. Prenderlo es una decisión consciente y el diálogo lo explica con esas
 *    palabras;
 *  - el resultado se queda en el mismo diálogo (revisadas, actualizadas y la lista de cambios), para
 *    que el admin vea qué pasó sin tener que buscarlo en el tablero.
 *
 * El botón que abre este diálogo solo aparece con rol ADMIN (el backend responde 403 igual).
 */

export interface RecalculateDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RecalculateDialog({ open, onClose }: RecalculateDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const recalc = useRecalculateLeads(tenantId);

  const [form, setForm] = useState<RecalculoFormState>(emptyRecalculoForm);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<RecalculoMasivoResultado | null>(null);

  /** Cada apertura arranca limpia: sin resultado de la vez anterior. */
  useEffect(() => {
    if (!open) return;

    setForm(emptyRecalculoForm());
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setResult(null);
  }, [open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateRecalculoForm(form.limite);
    if (hasFormErrors(nextErrors)) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      setResult(await recalc.mutateAsync(recalcularMasivoPayload(form)));
    } catch (error) {
      setErrors(formErrorsFromApiError(error, RECALCULO_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Recalcular los leads"
      description="Vuelve a clasificar las conversaciones con lo que dicen HOY sus mensajes."
      size="lg"
      footer={
        result ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={recalc.isPending}>
              Cancelar
            </Button>
            <Button type="submit" form="crm-recalculo-form" loading={recalc.isPending}>
              Recalcular ahora
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          <Alert tone="success">{summarizeRecalculo(result)}</Alert>

          <p className="text-caption text-ink-muted">
            {`Se revisaron con un tope de ${formatNumber(result.limite)} conversaciones. Las que no entraron quedan para la próxima corrida (sube el tope si quieres abarcar más de una vez).`}
          </p>

          {result.cambios.length === 0 ? (
            <p className="text-caption text-ink-muted">
              Ningún lead cambió de estado: el clasificador coincide con lo que ya estaba guardado.
            </p>
          ) : (
            <div className="space-y-2">
              <h3 className="text-caption font-semibold text-ink">
                {`${formatNumber(result.cambios.length)} ${
                  result.cambios.length === 1 ? 'lead cambió' : 'leads cambiaron'
                } de estado`}
              </h3>

              <ul className="scroll-thin max-h-64 space-y-1.5 overflow-y-auto">
                {result.cambios.map((cambio) => (
                  <li
                    key={cambio.conversationId}
                    className="rounded-panel border border-line px-3 py-2 text-caption text-ink-soft"
                  >
                    <span className="block font-mono text-micro text-ink-faint">
                      {cambio.conversationId}
                    </span>
                    {describeCambioLead(cambio)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <form id="crm-recalculo-form" onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormErrorAlert message={submitError} errors={errors} />

          <p className="text-caption text-ink-muted">
            Sirve para lo que ya existía antes de activar el CRM: el clasificador automático solo
            mira las conversaciones de a una, cuando llega un mensaje. El detalle de cada lead explica
            qué señales vio el clasificador hoy.
          </p>

          <Field
            label="Conversaciones a revisar"
            htmlFor="crmRecalculoLimite"
            required
            hint={`Entre 1 y ${formatNumber(RECALCULO_LIMIT_MAX)}. Por defecto 500.`}
            error={fieldError(errors, 'limite')}
          >
            <Input
              id="crmRecalculoLimite"
              name="limite"
              type="number"
              min={1}
              max={RECALCULO_LIMIT_MAX}
              step={1}
              inputMode="numeric"
              value={form.limite}
              invalid={Boolean(fieldError(errors, 'limite'))}
              onChange={(event) => setForm({ ...form, limite: event.target.value })}
            />
          </Field>

          <div className="flex items-start justify-between gap-4 rounded-panel border border-line px-3.5 py-3">
            <div className="min-w-0">
              <p className="text-caption font-medium text-ink">
                Incluir los leads reclasificados a mano
              </p>
              <p className="mt-0.5 text-micro text-ink-muted">
                Apagado (recomendado): lo que el equipo movió a mano no se toca, porque el
                clasificador no sabe que el cliente cerró por teléfono. Prendido: el recálculo pisa
                esas decisiones y las reemplaza por el análisis automático.
              </p>
            </div>

            <Switch
              checked={form.incluirManuales}
              label="Incluir los leads reclasificados a mano"
              onChange={(checked) => setForm({ ...form, incluirManuales: checked })}
            />
          </div>

          {form.incluirManuales ? (
            <Alert tone="warning">
              Con esta opción prendida se pierden las reclasificaciones manuales: los leads vuelven a
              quedar como los clasifique el bot. El historial guarda el cambio, pero el estado manual
              no se recupera con un clic.
            </Alert>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

